import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/functions'
import 'firebase/storage'

const { buildTelemetryReport } = require('./telemetryReport')
const {
  createContentAddressedTelemetryId,
  sha256Hex,
  validateTelemetryFile,
} = require('./telemetryIngestion')

const sourceExtension = source => source === 'alfano' ? 'zip' : 'ibt'
const sourceContentType = source => source === 'alfano' ? 'application/zip' : 'application/octet-stream'

export const sourceStoragePath = ({ uid, telemetryId, source }) =>
  `telemetry/${uid}/${telemetryId}/source.${sourceExtension(source)}`

export const uploadTelemetrySource = async ({ file, source, telemetryId }) => {
  const user = firebase.auth().currentUser
  if (!user) throw new Error('You must be logged in to save telemetry.')
  if (!file) throw new Error('A telemetry source file is required.')

  validateTelemetryFile(file, source)
  const sourceDigest = await sha256Hex(file)
  const id = telemetryId || await createContentAddressedTelemetryId(source, user.uid, sourceDigest)

  const storagePath = sourceStoragePath({ uid: user.uid, telemetryId: id, source })
  const fileRef = firebase.storage().ref(storagePath)
  // List the owner-scoped directory before probing metadata. This preserves
  // immutable originals without emitting a normal object-not-found 404 in the UI.
  const listing = await fileRef.parent.listAll()
  const existingRef = listing.items.find(item => item.name === fileRef.name)
  let snapshot
  if (existingRef) {
    const existing = await existingRef.getMetadata()
    if (existing.customMetadata?.sourceSha256 !== sourceDigest || Number(existing.size) !== Number(file.size)) {
      throw new Error('A different source file already exists at this telemetry path.')
    }
    snapshot = existing
  } else {
    snapshot = await fileRef.put(file, {
      contentType: sourceContentType(source),
      customMetadata: {
        source,
        telemetryId: id,
        sourceSha256: sourceDigest,
        originalName: file.name || `source.${sourceExtension(source)}`,
      },
    })
  }

  return {
    telemetryId: id,
    storagePath,
    sourceDigest,
    sourceFile: {
      name: file.name || null,
      size: file.size || snapshot.totalBytes || null,
      contentType: sourceContentType(source),
    },
  }
}

export const persistAlfanoTelemetry = async ({ file, telemetryData, selectedLapNumber, telemetryId }) => {
  const uploaded = await uploadTelemetrySource({ file, source: 'alfano', telemetryId })
  const id = uploaded.telemetryId
  const derivedReport = buildTelemetryReport({
    source: 'alfano',
    allLaps: telemetryData.allLaps,
    selectedLapNumber,
    trackInfo: telemetryData.trackInfo || {},
    file: uploaded.sourceFile,
  })

  const functions = firebase.functions()
  if (process.env.NODE_ENV === 'development') functions.useEmulator('localhost', 5001)
  const storeTelemetry = functions.httpsCallable('storeTelemetry')
  const result = await storeTelemetry({
    telemetry: {
      telemetryId: id,
      source: 'alfano',
      storagePath: uploaded.storagePath,
      sourceDigest: uploaded.sourceDigest,
      selectedLapNumber,
    },
  })

  return {
    id: result.data?.id || id,
    storagePath: uploaded.storagePath,
    sourceFile: uploaded.sourceFile,
    derivedReport,
  }
}
